"use client";

import Image from "next/image";
import Link from "next/link";
import ScrollReveal from "@/components/common/ScrollReveal";

interface FooterLink {
  name: string;
  href: string;
}

interface FooterColumn {
  heading: string;
  headingHref?: string;
  links?: FooterLink[];
}

const COLUMNS: FooterColumn[] = [
  {
    heading: "SERVICES",
    headingHref: "/service",
    links: [
      // Category landing pages were removed — point to each category's first service.
      // Manage was dropped from the Header mega menu; keep the footer in sync.
      { name: "Offend", href: "/service/offend/web-application" },
      { name: "Defend", href: "/service/defend/application-security-analysis" },
    ],
  },
  {
    heading: "PRODUCTS",
    // No /products landing page, so the heading points at the first product —
    // the same entry point the PRODUCTS nav item uses.
    headingHref: "/products/hackgrid",
    // Order and labels mirror the Header mega menu's left column (Header.tsx
    // productItems `menuLabel`) and the Products page tab row: bare product
    // names in caps, no descriptor.
    links: [
      { name: "HACKGRID", href: "/products/hackgrid" },
      { name: "SHRED", href: "/products/shred" },
      { name: "Q-SUITE", href: "/products/q-suite" },
      { name: "DARKSIGHT", href: "/products/darksight" },
    ],
  },
  {
    heading: "OT SECURITY",
    // There is no /ot-security landing page — both tabs are sub-pages — so the
    // heading points at the first one like the Header nav item does.
    headingHref: "/ot-security/ot-ics-simulation",
    // Same two entries, same order, as the Header's OT SECURITY menu
    // (Header.tsx SECTION_MENUS) and the section's tab bar.
    links: [
      { name: "OT/ICS Simulation", href: "/ot-security/ot-ics-simulation" },
      { name: "AID ICS Framework", href: "/ot-security/aid-ics-framework" },
    ],
  },
  {
    heading: "RESOURCES",
    headingHref: "/resources",
    links: [
      { name: "Zero-day Tracker", href: "/resources/zero-day-tracker" },
      { name: "Advisories", href: "/resources/security-advisories" },
      { name: "White Paper", href: "/resources/white-paper" },
    ],
  },
  {
    heading: "COMPANY",
    headingHref: "/company/about-us",
    links: [
      { name: "About Us", href: "/company/about-us" },
      { name: "Team", href: "/company/team" },
      { name: "Customers", href: "/company/customers" },
      // Careers hidden on request — see src/app/company/layout.tsx.
    ],
  },
];

// Each glyph fills a different share of its own viewBox (the Facebook "f" is
// narrow, the YouTube badge is wide and uses a 24 viewBox), so the render box is
// tuned per platform to keep every glyph ~14px optically in the 32px circle —
// the same glyph/circle ratio as V1.
const SOCIAL_ICONS = [
  {
    name: "Facebook",
    icon: "/images/Footer/Facebook.svg",
    href: "https://www.facebook.com/ecqnet",
    size: 17,
  },
  {
    name: "X",
    icon: "/images/Footer/x.svg",
    href: "https://twitter.com/ECQ_Official",
    size: 15,
  },
  {
    name: "LinkedIn",
    icon: "/images/Footer/LinkedIn.svg",
    href: "https://www.linkedin.com/company/e-cqurity",
    size: 16,
  },
  {
    name: "YouTube",
    icon: "/images/Footer/Youtube.svg",
    href: "https://www.youtube.com/channel/UCc1JL5rK9dwyfsIOyGH8MXw",
    size: 15,
  },
];

const CERTIFICATIONS = [
  { name: "ISO/IEC 27001", image: "/images/Footer/ISO_IEC_27001.svg" },
  { name: "CREST", image: "/images/Footer/Crest.svg" },
];

export default function Footer() {
  return (
    <footer className="relative z-10 w-full bg-surface-footer">
      {/* Same gutters as the page body (px-4 sm:px-6 lg:px-8 around a 1300px box):
          that reproduces the Figma 70px margin at the 1440px artboard while still
          tracking the page column below it. The old fixed md:px-[70px] only lined
          up at exactly 1440 and left the footer 38-46px inboard on laptops/tablets. */}
      <div className="px-4 sm:px-6 lg:px-8 pt-10 pb-6 sm:pt-12 sm:pb-8">
        <div className="max-w-[1300px] mx-auto">
          <ScrollReveal direction="up" delay={0}>
            {/* Two states only, split at the site's single mobile/desktop line
                (lg / 1024px, the same one the header nav uses): a 2-column grid
                below, one row of 5 columns above. The old md:grid-cols-3 added a
                768–1023px band where the 5 headings sat 3 + 2 — a third layout
                that matched neither side. */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-8 sm:gap-x-8 sm:gap-y-10 lg:flex lg:justify-between lg:items-start lg:gap-0">
              {COLUMNS.map((col) => (
                <div key={col.heading} className="flex flex-col gap-3 sm:gap-4">
                  {col.headingHref ? (
                    <Link
                      href={col.headingHref}
                      className="body-1-heavy text-text-highlight hover:text-primary transition-colors"
                    >
                      {col.heading}
                    </Link>
                  ) : (
                    <span className="body-1-heavy text-text-highlight">
                      {col.heading}
                    </span>
                  )}
                  {col.links && (
                    <ul className="flex flex-col gap-3 sm:gap-4">
                      {col.links.map((link) => (
                        <li key={link.name}>
                          <Link
                            href={link.href}
                            className="body-1 text-neutral hover:text-primary transition-colors"
                          >
                            {link.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}

              <div className="flex flex-col gap-3 sm:gap-4">
                <Link
                  href="/contact/vietnam"
                  className="body-1-heavy text-text-highlight hover:text-primary transition-colors w-fit"
                >
                  CONTACT
                </Link>
                {/* Below sm the column is only half of a 2-col grid (~140px at
                    360px wide), too narrow for four 32px circles at a fixed gap:
                    they shrink to 28px and justify-between spreads the leftover
                    space, so the row always fits. shrink-0 keeps them round —
                    without it flex squashes them into vertical pills. */}
                <div className="flex flex-nowrap items-center justify-between gap-0.5 sm:justify-start sm:gap-5">
                  {SOCIAL_ICONS.map((social) => (
                    <Link
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.name}
                      className="w-7 h-7 sm:w-8 sm:h-8 shrink-0 rounded-full flex items-center justify-center bg-neutral hover:bg-primary transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-footer"
                    >
                      <Image
                        src={social.icon}
                        alt=""
                        width={social.size}
                        height={social.size}
                        style={{ width: social.size, height: social.size }}
                        className="shrink-0 object-contain brightness-0 invert"
                      />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={100}>
            {/* max-w, not w: at lg the footer column is only ~884px wide, so a fixed
                966px broke out of the column and made the whole page scroll
                horizontally between 1024 and 1105px. */}
            <div className="mt-8 flex w-full flex-col gap-3 sm:gap-4 lg:max-w-[966px]">
              <h3 className="headline-4 text-text-highlight">CERTIFICATION</h3>
              {/* Below sm the tiles go into a 2-col grid instead of wrapping:
                  with flex-wrap + a fixed 200px tile each one claims its own
                  row, stacking the footer taller than it needs to be. Two
                  columns give ~171px tiles — only 15% narrower than desktop,
                  so the logos stay legible — and the two certifications land
                  on one row at every width. */}
              <div className="grid grid-cols-2 gap-4 sm:flex sm:flex-wrap sm:gap-8">
                {CERTIFICATIONS.map((cert) => (
                  <div
                    key={cert.name}
                    // aspect ratio below sm keeps the desktop 200:105 tile shape
                    // while the width comes from the grid column.
                    className="w-full aspect-[200/105] sm:w-[200px] sm:h-[105px] sm:aspect-auto border-[1.5px] border-[#979797] rounded-lg flex items-center justify-center p-3 sm:p-4 hover:border-primary transition-colors"
                  >
                    <Image
                      src={cert.image}
                      alt={cert.name}
                      width={180}
                      height={85}
                      className="max-w-full max-h-full w-auto h-auto object-contain"
                    />
                  </div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>

      <div className="bg-surface-footer-deep px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="max-w-[1300px] mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          {/* max-w, not w: a hard width that only exists from sm up (with no
              base) is a latent bug — this is a copyright line, not a fixed
              column, so it should cap rather than reserve 347px. */}
          <p
            className="body-2 text-text-default sm:max-w-[347px]"
            suppressHydrationWarning
          >
            Copyright © {new Date().getFullYear()} ECQ. All Rights Reserved.
          </p>
          {/* Both policy pages live here. /cookies used to be reachable only
              from the sitemap — no link anywhere in the UI — even though the
              cookie banner's "Learn more" now sends people to it. Wraps on
              narrow screens so the pair never pushes the copyright line. */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link
              href="/privacy"
              className="body-1 text-text-default hover:text-primary transition-colors uppercase"
            >
              Privacy Policy
            </Link>
            <Link
              href="/cookies"
              className="body-1 text-text-default hover:text-primary transition-colors uppercase"
            >
              Cookie Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
